import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  SelectChangeEvent,
} from '@mui/material';

interface User {
  id: string;
  name: string;
}

interface UserFilterProps {
  users: User[];
  selectedUserIds: string[];
  onChange: (userIds: string[]) => void;
  loading?: boolean;
}

const UserFilter: React.FC<UserFilterProps> = ({
  users,
  selectedUserIds,
  onChange,
  loading = false,
}) => {
  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onChange(typeof value === 'string' ? value.split(',') : value);
  };

  const getSelectedNames = () => {
    if (selectedUserIds.length === 0) return 'All Users';
    return selectedUserIds
      .map((id) => users.find((u) => u.id === id)?.name || id)
      .join(', ');
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Users
      </Typography>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <Select
          multiple
          value={selectedUserIds}
          onChange={handleChange}
          input={<OutlinedInput />}
          renderValue={(selected) => {
            if (selected.length === 0) {
              return <em>All Users</em>;
            }
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.slice(0, 2).map((id) => (
                  <Chip
                    key={id}
                    label={users.find((u) => u.id === id)?.name || id}
                    size="small"
                  />
                ))}
                {selected.length > 2 && (
                  <Chip label={`+${selected.length - 2}`} size="small" />
                )}
              </Box>
            );
          }}
          displayEmpty
          disabled={loading}
        >
          <MenuItem value="" disabled>
            <em>Select users to filter</em>
          </MenuItem>
          {users.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              <Checkbox checked={selectedUserIds.includes(user.id)} />
              <ListItemText primary={user.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default UserFilter;
